/**
 * 文件简介：
 * - 脑波波形 waveData 工具：冻结数据点、原地 trim、刷新时引用 adopt
 * - 避免 Vue2 对 { value, timestamp } 深度响应式化导致 Dep/Observer 泄漏
 * 使用方法：写入 waveData 时用 createWavePoint；刷新 merge 时用 adoptWaveData
 */

export const WAVE_BANDS = ['delta', 'theta', 'alpha', 'beta', 'gamma'];

/**
 * 创建不可响应式数据点（Vue2 对 Object.freeze 对象不会 observe）
 * @param {number} value
 * @param {number} [timestamp]
 * @param {object} [extra] 可选扩展字段（如 interpolated）
 */
export function createWavePoint(value, timestamp = Date.now(), extra = null) {
  const point = { value, timestamp };
  if (extra && typeof extra === 'object') {
    Object.assign(point, extra);
  }
  return Object.freeze(point);
}

/**
 * 原地 trim 波线数组，不新建数组
 * @param {Array} arr
 * @param {number} maxLen
 */
export function trimWaveBandInPlace(arr, maxLen) {
  if (!Array.isArray(arr) || arr.length <= maxLen) return;
  arr.splice(0, arr.length - maxLen);
}

/**
 * 判断 waveData 是否至少有一个频段有数据
 * @param {object|null} waveData
 */
export function hasWaveDataContent(waveData) {
  if (!waveData) return false;
  return WAVE_BANDS.some((band) => Array.isArray(waveData[band]) && waveData[band].length > 0);
}

/**
 * 刷新/merge 时保留引用：target 直接接管 source 的 waveData
 * @param {object} target
 * @param {object} source
 */
export function adoptWaveData(target, source) {
  if (!target || !source?.waveData) return;
  target.waveData = source.waveData;
}

/**
 * 确保 appointment 上有 waveData 结构（空数组壳）
 * @param {object} item
 */
export function ensureWaveDataShell(item) {
  if (!item) return;
  if (!item.waveData || typeof item.waveData !== 'object') {
    item.waveData = {};
  }
  WAVE_BANDS.forEach((band) => {
    if (!Array.isArray(item.waveData[band])) {
      item.waveData[band] = [];
    }
  });
}

/**
 * 仅拷贝数组壳，元素引用不变（protectedVirtualData 等需隔离数组引用时使用）
 * @param {object|null} waveData
 */
export function shallowCopyWaveDataShell(waveData) {
  if (!waveData) {
    return {
      delta: [],
      theta: [],
      alpha: [],
      beta: [],
      gamma: []
    };
  }
  const out = {};
  WAVE_BANDS.forEach((band) => {
    out[band] = Array.isArray(waveData[band]) ? waveData[band] : [];
  });
  return out;
}

/**
 * merge 时优先保留已有 waveData 引用；双方都有数据时原地拼接并 trim
 * @param {object} newItem
 * @param {object} existingItem
 * @param {number} [maxLen]
 */
export function mergeWaveDataPreferExisting(newItem, existingItem, maxLen = 300) {
  if (!newItem || !existingItem?.waveData) return;

  const oldHas = hasWaveDataContent(existingItem.waveData);
  if (!oldHas) return;

  if (!hasWaveDataContent(newItem.waveData)) {
    adoptWaveData(newItem, existingItem);
    return;
  }

  WAVE_BANDS.forEach((band) => {
    const oldArr = existingItem.waveData[band];
    const newArr = newItem.waveData[band];
    if (!Array.isArray(oldArr) || oldArr.length === 0) return;
    if (!Array.isArray(newArr) || newArr.length === 0) {
      newItem.waveData[band] = oldArr;
      return;
    }
    newArr.unshift(...oldArr);
    trimWaveBandInPlace(newArr, maxLen);
  });
}

/**
 * 原地压缩波线：均匀采样保留 targetLength 个点（不 spread 新建对象）
 * @param {Array} data 原地修改的数组
 * @param {number} targetLength
 */
export function compressWaveBandInPlace(data, targetLength) {
  if (!data || !Array.isArray(data) || data.length === 0) return;
  if (data.length <= targetLength) return;

  const indices = new Set();
  indices.add(0);
  indices.add(data.length - 1);

  const values = data.map((point) => {
    if (typeof point === 'object' && point !== null) {
      return point.value !== undefined ? point.value : point;
    }
    return point;
  });

  let maxIndex = 0;
  let minIndex = 0;
  let maxValue = values[0];
  let minValue = values[0];

  for (let i = 1; i < values.length; i++) {
    if (values[i] > maxValue) {
      maxValue = values[i];
      maxIndex = i;
    }
    if (values[i] < minValue) {
      minValue = values[i];
      minIndex = i;
    }
  }

  if (maxIndex !== 0 && maxIndex !== data.length - 1) {
    indices.add(maxIndex);
  }
  if (minIndex !== 0 && minIndex !== data.length - 1 && minIndex !== maxIndex) {
    indices.add(minIndex);
  }

  const remainingPoints = targetLength - indices.size;
  if (remainingPoints > 0) {
    const step = (data.length - 1) / (remainingPoints + 1);
    for (let i = 0; i < remainingPoints; i++) {
      let candidateIndex = Math.round((i + 1) * step);
      while (candidateIndex < data.length && indices.has(candidateIndex)) {
        candidateIndex++;
      }
      if (candidateIndex < data.length) {
        indices.add(candidateIndex);
      }
    }
  }

  const sortedIndices = Array.from(indices).sort((a, b) => a - b).slice(0, targetLength);
  if (sortedIndices[0] !== 0) sortedIndices[0] = 0;
  if (sortedIndices[sortedIndices.length - 1] !== data.length - 1) {
    sortedIndices[sortedIndices.length - 1] = data.length - 1;
  }

  const uniqueIndices = [...new Set(sortedIndices)].sort((a, b) => a - b);
  const sampled = uniqueIndices.map((index) => data[index]);
  data.splice(0, data.length, ...sampled);
}
